// VIN Lookup Service for Vehicle Data
export interface VehicleData {
  vin: string;
  make: string;
  model: string;
  year: number;
  trim?: string;
  engine?: string;
  transmission?: string;
  fuelType?: string;
  bodyType?: string;
  drivetrain?: string;
  doors?: number;
  seats?: number;
  color?: string;
  manufacturer?: string;
  countryOfOrigin?: string;
}

export interface VinLookupResponse {
  success: boolean;
  data?: VehicleData;
  error?: string;
}

// Mock VIN database for demonstration
const mockVinDatabase: { [key: string]: Partial<VehicleData> } = {
  '1HGBH41JXMN109186': {
    make: 'Honda',
    model: 'Civic',
    year: 2021,
    trim: 'EX',
    engine: '2.0L 4-Cylinder',
    transmission: 'CVT Automatic',
    fuelType: 'Gasoline',
    bodyType: 'Sedan',
    drivetrain: 'FWD',
    doors: 4,
    seats: 5,
    manufacturer: 'Honda Motor Company',
    countryOfOrigin: 'Japan'
  },
  '7FRKN3G5RMG134752': {
    make: 'Toyota',
    model: 'Camry',
    year: 2023,
    trim: 'LE',
    engine: '2.5L 4-Cylinder',
    transmission: '8-Speed Automatic',
    fuelType: 'Gasoline',
    bodyType: 'Sedan',
    drivetrain: 'FWD',
    doors: 4,
    seats: 5,
    manufacturer: 'Toyota Motor Corporation',
    countryOfOrigin: 'Japan'
  },
  '5NPE34AF4HH012345': {
    make: 'Hyundai',
    model: 'Sonata',
    year: 2022,
    trim: 'SEL',
    engine: '2.5L 4-Cylinder',
    transmission: '8-Speed Automatic',
    fuelType: 'Gasoline',
    bodyType: 'Sedan',
    drivetrain: 'FWD',
    doors: 4,
    seats: 5,
    manufacturer: 'Hyundai Motor Company',
    countryOfOrigin: 'South Korea'
  },
  'WBAJB1C58KVA12345': {
    make: 'BMW',
    model: '3 Series',
    year: 2023,
    trim: '330i',
    engine: '2.0L Turbocharged 4-Cylinder',
    transmission: '8-Speed Automatic',
    fuelType: 'Gasoline',
    bodyType: 'Sedan',
    drivetrain: 'RWD',
    doors: 4,
    seats: 5,
    manufacturer: 'BMW AG',
    countryOfOrigin: 'Germany'
  },
  'WBA8E9G58KN123456': {
    make: 'BMW',
    model: 'X5',
    year: 2023,
    trim: 'xDrive40i',
    engine: '3.0L Turbocharged 6-Cylinder',
    transmission: '8-Speed Automatic',
    fuelType: 'Gasoline',
    bodyType: 'SUV',
    drivetrain: 'AWD',
    doors: 4,
    seats: 7,
    manufacturer: 'BMW AG',
    countryOfOrigin: 'Germany'
  }
};

// Popular makes and models for dropdowns
export const vehicleMakes = [
  'Acura', 'Audi', 'BMW', 'Buick', 'Cadillac', 'Chevrolet', 'Chrysler', 
  'Dodge', 'Ford', 'Genesis', 'GMC', 'Honda', 'Hyundai', 'Infiniti',
  'Jaguar', 'Jeep', 'Kia', 'Land Rover', 'Lexus', 'Lincoln', 'Mazda',
  'Mercedes-Benz', 'Mitsubishi', 'Nissan', 'Porsche', 'Ram', 'Subaru',
  'Tesla', 'Toyota', 'Volkswagen', 'Volvo'
];

export const vehicleModelsByMake: { [key: string]: string[] } = {
  'Toyota': ['Camry', 'Corolla', 'Prius', 'Highlander', 'RAV4', 'Sienna', 'Tacoma', 'Tundra', 'Venza', 'Yaris'],
  'Honda': ['Accord', 'Civic', 'CR-V', 'HR-V', 'Odyssey', 'Passport', 'Pilot', 'Ridgeline'],
  'BMW': ['2 Series', '3 Series', '4 Series', '5 Series', '7 Series', 'X1', 'X3', 'X5', 'X7', 'Z4'],
  'Mercedes-Benz': ['A-Class', 'C-Class', 'E-Class', 'S-Class', 'GLA', 'GLB', 'GLC', 'GLE', 'GLS'],
  'Audi': ['A3', 'A4', 'A6', 'A8', 'Q3', 'Q5', 'Q7', 'Q8', 'e-tron'],
  'Ford': ['F-150', 'Mustang', 'Explorer', 'Escape', 'Edge', 'Bronco', 'Ranger', 'Transit'],
  'Chevrolet': ['Silverado', 'Equinox', 'Tahoe', 'Suburban', 'Malibu', 'Camaro', 'Corvette'],
  'Nissan': ['Altima', 'Sentra', 'Rogue', 'Murano', 'Pathfinder', 'Titan', 'Leaf', 'GT-R'],
  'Hyundai': ['Elantra', 'Sonata', 'Tucson', 'Santa Fe', 'Palisade', 'Kona', 'Ioniq'],
  'Kia': ['Forte', 'Optima', 'Sorento', 'Telluride', 'Sportage', 'Soul', 'Stinger']
};

// Validate VIN format
export const isValidVin = (vin: string): boolean => {
  // VIN should be 17 characters, alphanumeric, no I, O, or Q
  const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/;
  return vinRegex.test(vin.toUpperCase());
};

// Decode VIN for basic info (simplified version)
export const decodeVinBasic = (vin: string): { year?: number; region?: string } => {
  if (!isValidVin(vin)) return {};
  
  const year = getYearFromVin(vin);
  const region = getRegionFromVin(vin);
  
  return { year, region };
};

const getYearFromVin = (vin: string): number => {
  const yearCode = vin.charAt(9);
  const yearMap: { [key: string]: number } = {
    'A': 2010, 'B': 2011, 'C': 2012, 'D': 2013, 'E': 2014, 'F': 2015,
    'G': 2016, 'H': 2017, 'J': 2018, 'K': 2019, 'L': 2020, 'M': 2021,
    'N': 2022, 'P': 2023, 'R': 2024, 'S': 2025, 'T': 2026, 'V': 2027,
    'W': 2028, 'X': 2029, 'Y': 2030
  };
  
  return yearMap[yearCode] || new Date().getFullYear();
};

const getRegionFromVin = (vin: string): string => {
  const firstChar = vin.charAt(0);
  if (['1', '4', '5'].includes(firstChar)) return 'North America';
  if (['J', 'K', 'L', 'M', 'N', 'P', 'R'].includes(firstChar)) return 'Asia';
  if (['S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'].includes(firstChar)) return 'Europe';
  return 'Unknown';
};

// Main VIN lookup function - integrates with your existing VIN API service
export const lookupVin = async (vin: string): Promise<VinLookupResponse> => {
  try {
    if (!isValidVin(vin)) {
      return {
        success: false,
        error: 'Invalid VIN format. VIN must be 17 characters long and contain only letters and numbers (excluding I, O, Q).'
      };
    }

    // TODO: Replace this URL and headers with your actual VIN API service
    const VIN_API_URL = import.meta.env.VITE_VIN_API_URL || 'https://api.your-vin-service.com';
    const VIN_API_KEY = import.meta.env.VITE_VIN_API_KEY || '';

    // Call your actual VIN API service
    try {
      const response = await fetch(`${VIN_API_URL}/decode/${vin}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${VIN_API_KEY}`,
          'Content-Type': 'application/json',
          // Add any other headers your VIN service requires
        }
      });

      if (response.ok) {
        const apiData = await response.json();
        
        // TODO: Adjust this mapping based on your VIN API response format
        if (apiData.success && apiData.data) {
          return {
            success: true,
            data: {
              vin: vin.toUpperCase(),
              make: apiData.data.make || '',
              model: apiData.data.model || '',
              year: apiData.data.year || new Date().getFullYear(),
              trim: apiData.data.trim,
              engine: apiData.data.engine,
              transmission: apiData.data.transmission,
              fuelType: apiData.data.fuelType,
              bodyType: apiData.data.bodyType,
              drivetrain: apiData.data.drivetrain,
              doors: apiData.data.doors,
              seats: apiData.data.seats || 4,
              manufacturer: apiData.data.manufacturer,
              countryOfOrigin: apiData.data.countryOfOrigin
            }
          };
        }
      }
    } catch (apiError) {
    }

    // Fallback to mock database for development/testing
    const mockData = mockVinDatabase[vin.toUpperCase()];
    if (mockData) {
      return {
        success: true,
        data: {
          vin: vin.toUpperCase(),
          ...mockData
        } as VehicleData
      };
    }

    // If not in mock database, try to decode basic info
    const basicInfo = decodeVinBasic(vin);
    
    // Return basic decoded info
    return {
      success: true,
      data: {
        vin: vin.toUpperCase(),
        year: basicInfo.year || new Date().getFullYear(),
        make: 'Unknown',
        model: 'Unknown',
        countryOfOrigin: basicInfo.region || 'Unknown'
      }
    };

  } catch (error) {
    return {
      success: false,
      error: 'VIN lookup service temporarily unavailable. Please enter vehicle information manually.'
    };
  }
};

// Get models for a specific make
export const getModelsForMake = (make: string): string[] => {
  return vehicleModelsByMake[make] || [];
};

// Search makes
export const searchMakes = (query: string): string[] => {
  if (!query) return vehicleMakes;
  return vehicleMakes.filter(make => 
    make.toLowerCase().includes(query.toLowerCase())
  );
};

// Search models for a specific make
export const searchModels = (make: string, query: string): string[] => {
  const models = getModelsForMake(make);
  if (!query) return models;
  return models.filter(model => 
    model.toLowerCase().includes(query.toLowerCase())
  );
};